/**
 * Admin V3 — Devis detail page
 *
 * Source: GET /admin/devis/:id → { devis }  (ADMIN_PARTNER_READ).
 * Full lifecycle through the UI (all POST = ADMIN_PARTNER_UPDATE):
 *   send / accept / reject / cancel / expire / revise / convert(+booking),
 *   create Bon de commande (POST /admin/bon-de-commande), Download PDF (read).
 *
 * RBAC: mutation affordances hidden unless can('admin:partner:update');
 * backend @rbac_permission is authoritative. Lineage (version / root / supersedes
 * / converted-to) is shown for traceability.
 */

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FileText, ArrowLeft, Download, Send, CheckCircle2, XCircle, Ban, History, ArrowRightCircle, ClipboardList, Loader2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { SectionCard, SmallPill, currency, devisStatusMeta, formatAddressLines, cn } from '../../shared/visualTokens';
import useAdminQuery from '../../shared/hooks/useAdminQuery';
import useAdminMutation from '../../shared/hooks/useAdminMutation';
import usePermissions from '../../shared/hooks/usePermissions';
import usePdfDownload from '../../shared/hooks/usePdfDownload';
import { useToast } from '../../shared/hooks/useToast';
import { t, DEFAULT_LOCALE } from '../../i18n';

const PERM_UPDATE = 'admin:partner:update';

// Direct Devis→Booking is a LEGACY path. The ratified workflow is
// Devis → Bon de commande → Réservation. The action is hidden by default and
// only shown when explicitly re-enabled (mirrors backend
// COMMERCIAL_ALLOW_DIRECT_DEVIS_BOOKING). Backend remains authoritative (409).
const ALLOW_DIRECT_DEVIS_BOOKING =
  import.meta.env.VITE_COMMERCIAL_ALLOW_DIRECT_DEVIS_BOOKING === 'true';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Intl.DateTimeFormat('fr-FR', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(typeof iso === 'string' ? new Date(iso) : iso); }
  catch { return String(iso); }
};

const Field = ({ label, children, accent }) => (
  <div className="text-[12px]">
    <dt className="text-slate-400 font-semibold uppercase tracking-wider text-[10px]">{label}</dt>
    <dd className={cn('font-medium break-all', accent || 'text-slate-700')}>{children}</dd>
  </div>
);

const DevisDetailPage = () => {
  const locale = DEFAULT_LOCALE;
  const { devisId } = useParams();
  const navigate = useNavigate();
  const { can, loading: permsLoading } = usePermissions();
  const { showSuccess, showError } = useToast();

  const { data, loading, error, refetch } = useAdminQuery(`/admin/devis/${devisId}`);
  const devis = data?.devis || null;

  const sendM = useAdminMutation(`/admin/devis/${devisId}/send`, 'post');
  const acceptM = useAdminMutation(`/admin/devis/${devisId}/accept`, 'post');
  const rejectM = useAdminMutation(`/admin/devis/${devisId}/reject`, 'post');
  const cancelM = useAdminMutation(`/admin/devis/${devisId}/cancel`, 'post');
  const expireM = useAdminMutation(`/admin/devis/${devisId}/expire`, 'post');
  const reviseM = useAdminMutation(`/admin/devis/${devisId}/revise`, 'post');
  const convertM = useAdminMutation(`/admin/devis/${devisId}/convert`, 'post');
  const createBdcM = useAdminMutation('/admin/bon-de-commande', 'post');
  const pdf = usePdfDownload();

  const [reasonMode, setReasonMode] = useState(null); // 'reject' | 'cancel' | null
  const [reason, setReason] = useState('');

  const canUpdate = can(PERM_UPDATE);
  const s = devis?.status;
  const isDraft = s === 'draft';
  const isSent = s === 'sent';
  const isAccepted = s === 'accepted';
  const canCancel = ['draft', 'sent', 'accepted'].includes(s);
  const canRevise = ['sent', 'accepted', 'rejected', 'expired'].includes(s);

  const onErr = (e) => showError(e?.response?.data?.detail || t('devis.action.error', locale));
  const after = (msg) => ({ onSuccess: () => { showSuccess(msg); refetch(); }, onError: onErr });

  const handlePdf = () => pdf.download(`/admin/devis/${devisId}/pdf`, `devis-${devis?.devis_number || 'draft'}.pdf`)
    .then((ok) => { if (!ok) showError(pdf.error || t('devis.action.pdfError', locale)); });

  const handleSend = () => sendM.mutate({}, after(t('devis.action.sentOk', locale)));
  const handleAccept = () => acceptM.mutate({ method: 'operator_manual' }, after(t('devis.action.acceptedOk', locale)));
  const handleExpire = () => expireM.mutate({}, after(t('devis.action.expiredOk', locale)));
  const handleConvert = () => convertM.mutate({}, {
    onSuccess: (res) => { showSuccess(t('devis.action.convertedOk', locale).replace('{ref}', res?.booking?.booking_reference || '')); refetch(); },
    onError: onErr,
  });
  const handleRevise = () => reviseM.mutate({}, {
    onSuccess: (res) => { showSuccess(t('devis.action.revisedOk', locale)); const id = res?.devis?.id || res?.id; if (id) navigate(`/admin/billing/devis/${id}`); else refetch(); },
    onError: onErr,
  });
  const handleReason = () => {
    const m = reasonMode === 'reject' ? rejectM : cancelM;
    const msg = reasonMode === 'reject' ? t('devis.action.rejectedOk', locale) : t('devis.action.cancelledOk', locale);
    m.mutate({ reason }, { onSuccess: () => { showSuccess(msg); setReasonMode(null); setReason(''); refetch(); }, onError: onErr });
  };
  const handleCreateBdc = () => createBdcM.mutate({ devis_id: devisId }, {
    onSuccess: (res) => { showSuccess(t('devis.action.bdcCreatedOk', locale)); const id = res?.bon_de_commande?.id || res?.id; if (id) navigate(`/admin/billing/commande/${id}`); },
    onError: onErr,
  });

  const lines = Array.isArray(devis?.line_items) ? devis.line_items : [];
  const taxLabel = t('devis.field.tax', locale).replace('{rate}', String(devis?.indicative_tax_rate ?? 0));

  return (
    <SectionCard
      title={devis?.devis_number || t('devis.detail.back', locale)} icon={FileText} iconClass="bg-indigo-100 text-indigo-600"
      action={
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/billing/devis')} data-testid="adminv3-devis-detail-back">
          <ArrowLeft className="h-4 w-4 mr-1.5" />{t('devis.detail.back', locale)}
        </Button>
      }
    >
      {loading && !data && (
        <div className="flex items-center gap-2 text-slate-400 text-sm py-8 justify-center"><Loader2 className="h-4 w-4 animate-spin" />{t('devis.detail.loading', locale)}</div>
      )}
      {error && !loading && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700" data-testid="adminv3-devis-detail-error">{error}</div>
      )}

      {devis && (
        <div className="space-y-5">
          {/* Status + actions */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <SmallPill meta={devisStatusMeta[s] || devisStatusMeta.draft} />
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={handlePdf} disabled={pdf.loading} data-testid="adminv3-devis-pdf">
                {pdf.loading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Download className="h-3.5 w-3.5 mr-1.5" />}{t('devis.action.pdf', locale)}
              </Button>
              {/* F2: while the permissions fetch is in flight, show a stable spinner
                  placeholder so mutation buttons appear once (resolved) instead of
                  flickering in after the read-only PDF button. */}
              {permsLoading && (
                <span className="flex items-center px-2 text-slate-300" data-testid="adminv3-devis-perms-loading">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                </span>
              )}
              {canUpdate && isDraft && (
                <Button variant="default" size="sm" onClick={handleSend} disabled={sendM.loading} data-testid="adminv3-devis-send">
                  <Send className="h-3.5 w-3.5 mr-1.5" />{t('devis.action.send', locale)}
                </Button>
              )}
              {canUpdate && isSent && (
                <Button variant="default" size="sm" onClick={handleAccept} disabled={acceptM.loading} data-testid="adminv3-devis-accept">
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />{t('devis.action.accept', locale)}
                </Button>
              )}
              {canUpdate && isSent && (
                <Button variant="outline" size="sm" onClick={() => { setReasonMode('reject'); setReason(''); }} className="border-rose-200 text-rose-700 hover:bg-rose-50" data-testid="adminv3-devis-reject-toggle">
                  <XCircle className="h-3.5 w-3.5 mr-1.5" />{t('devis.action.reject', locale)}
                </Button>
              )}
              {canUpdate && isSent && (
                <Button variant="ghost" size="sm" onClick={handleExpire} disabled={expireM.loading} data-testid="adminv3-devis-expire">
                  {t('devis.action.expire', locale)}
                </Button>
              )}
              {canUpdate && isAccepted && ALLOW_DIRECT_DEVIS_BOOKING && (
                <Button variant="default" size="sm" onClick={handleConvert} disabled={convertM.loading} data-testid="adminv3-devis-convert">
                  <ArrowRightCircle className="h-3.5 w-3.5 mr-1.5" />{t('devis.action.convert', locale)}
                </Button>
              )}
              {canUpdate && isAccepted && (
                <Button variant="outline" size="sm" onClick={handleCreateBdc} disabled={createBdcM.loading} data-testid="adminv3-devis-create-bdc">
                  <ClipboardList className="h-3.5 w-3.5 mr-1.5" />{t('devis.action.createBdc', locale)}
                </Button>
              )}
              {canUpdate && canRevise && (
                <Button variant="outline" size="sm" onClick={handleRevise} disabled={reviseM.loading} data-testid="adminv3-devis-revise">
                  <History className="h-3.5 w-3.5 mr-1.5" />{t('devis.action.revise', locale)}
                </Button>
              )}
              {canUpdate && canCancel && (
                <Button variant="outline" size="sm" onClick={() => { setReasonMode('cancel'); setReason(''); }} className="border-rose-200 text-rose-700 hover:bg-rose-50" data-testid="adminv3-devis-cancel-toggle">
                  <Ban className="h-3.5 w-3.5 mr-1.5" />{t('devis.action.cancel', locale)}
                </Button>
              )}
            </div>
          </div>

          {/* Reason inline form (reject / cancel) */}
          {reasonMode && canUpdate && (
            <div className="rounded-xl border border-rose-200 bg-rose-50/50 p-4 space-y-2" data-testid="adminv3-devis-reason-form">
              <label className="text-[12px] font-semibold text-rose-700">
                {reasonMode === 'reject' ? t('devis.reject.reason', locale) : t('devis.cancel.reason', locale)}
              </label>
              <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} className="w-full rounded-lg border border-rose-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-rose-100" />
              <div className="flex gap-2">
                <Button variant="default" size="sm" onClick={handleReason} disabled={rejectM.loading || cancelM.loading}
                        className="bg-rose-600 hover:bg-rose-700" data-testid="adminv3-devis-reason-confirm">
                  {(rejectM.loading || cancelM.loading) && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}{t('devis.reason.confirm', locale)}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setReasonMode(null)}>{t('devis.create.cancel', locale)}</Button>
              </div>
            </div>
          )}

          {/* Customer */}
          <div className="rounded-xl border border-slate-200 p-4">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-2">{t('devis.section.customer', locale)}</div>
            <div className="text-slate-900 font-medium">{(devis.customer && (devis.customer.company || devis.customer.name)) || '—'}</div>
            {devis.customer?.name && devis.customer?.company && <div className="text-[12px] text-slate-500">{devis.customer.name}</div>}
            <div className="text-[12px] text-slate-500">{devis.customer?.email || ''}</div>
            <div className="text-[12px] text-slate-500 whitespace-pre-line">{formatAddressLines(devis.customer?.address)}</div>
            {devis.customer?.vat && <div className="text-[12px] text-slate-500 font-mono mt-1">{devis.customer.vat}</div>}
          </div>

          {/* Amounts */}
          <div className="rounded-xl border border-slate-200 p-4">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-3">{t('devis.section.amounts', locale)}</div>
            <dl className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3">
              <Field label={t('devis.field.subtotal', locale)}>{currency(devis.subtotal_ht ?? 0)}</Field>
              <Field label={taxLabel}>{currency(devis.indicative_tax_amount ?? 0)}</Field>
              <Field label={t('devis.field.total', locale)} accent="text-slate-900 font-bold">{currency(devis.total_ttc_estimated ?? 0)}</Field>
              <Field label={t('devis.field.validUntil', locale)}>{fmtDate(devis.valid_until)}</Field>
            </dl>
          </div>

          {/* Lineage */}
          <div className="rounded-xl border border-slate-200 p-4">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-3">{t('devis.section.lineage', locale)}</div>
            <dl className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3">
              <Field label={t('devis.field.version', locale)}>v{devis.version ?? 1}</Field>
              {devis.supersedes_id && <Field label={t('devis.field.supersedes', locale)}>{devis.supersedes_id}</Field>}
              {devis.superseded_by_id && <Field label={t('devis.field.supersededBy', locale)}>{devis.superseded_by_id}</Field>}
              {devis.converted_to_bdc_id && (
                <Field label={t('devis.field.convertedToBdc', locale)} accent="text-teal-700">
                  <button type="button" className="underline" onClick={() => navigate(`/admin/billing/commande/${devis.converted_to_bdc_id}`)} data-testid="adminv3-devis-lineage-bdc">{devis.converted_to_bdc_id}</button>
                </Field>
              )}
              {devis.converted_to_booking_id && <Field label={t('devis.field.convertedToBooking', locale)} accent="text-teal-700">{devis.converted_to_booking_id}</Field>}
            </dl>
          </div>

          {/* Line items */}
          {lines.length > 0 && (
            <div className="rounded-xl border border-slate-200 p-4">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-2">{t('devis.section.lineItems', locale)} ({lines.length})</div>
              <pre className="rounded-lg bg-slate-50 border border-slate-200 p-2 text-[11px] font-mono text-slate-600 overflow-x-auto max-h-56">
{JSON.stringify(lines, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </SectionCard>
  );
};

export default DevisDetailPage;
